import { inject, onScopeDispose, watch } from 'vue'
import { useRouter } from 'vue-router'

import { webAppRefererRouteInjectionKey } from '@/utils/injection-keys'
import { MCP_REDIRECT_PAYLOAD_KEY } from '@/utils/query-parameters'
import { useOboardMCP } from '@web-app/utils/mcp-utils'
import { useRefererRoute } from '@web-app/utils/route-helpers'
import { createExpirationTimestamp } from '@web-app/utils/stores-timestamp'
import { useSwitchOrganization } from '@web-app/utils/switch-organization-utils'

export const useLoginAppStores = ({ watchForRefererRoute = true } = {}) => {
  const router = useRouter()
  const _refererRoute = inject(webAppRefererRouteInjectionKey, null)

  const {
    isMcpLoginApp,
    redirectParamsState,
    resetMcpRedirectParamsStore,
    revokeMcpRedirectParams,
    validateMcpRedirectParams,
    isUserCameForMCPConnection,
    setState: setMcpRedirectParamsState
  } = useOboardMCP()

  const {
    refererRoute,
    refererRouteState,
    resolveRefererRoute,
    setState: setRefererRouteState
  } = useRefererRoute()

  const { switchOrganizationState, validateSwitchOrganizationState, resetSwitchOrganizationState } =
    useSwitchOrganization()

  if (watchForRefererRoute) {
    const { stop } = watch(
      _refererRoute,
      (newValue, oldValue) => {
        if (isUserCameForMCPConnection(newValue)) {
          setMcpRedirectParamsState({
            params: newValue.query[MCP_REDIRECT_PAYLOAD_KEY],
            expiry: createExpirationTimestamp()
          })

          return
        }

        if (
          !oldValue &&
          newValue &&
          newValue.hash &&
          newValue.fullPath &&
          newValue.fullPath !== '/'
        ) {
          const authProviderRoutes = router
            .getRoutes()
            .map(r => r.path)
            .filter(r => r !== '/')

          const isAuthProviderReferer = authProviderRoutes.some(route => {
            return newValue.fullPath.startsWith(route)
          })

          if (isAuthProviderReferer) return

          setRefererRouteState({
            refererRoute: newValue.fullPath,
            expiry: createExpirationTimestamp()
          })
        }
      },
      { immediate: true }
    )

    onScopeDispose(stop)
  }

  return {
    refererRouteState,
    redirectParamsState,
    refererRoute,
    resolveRefererRoute,
    isMcpLoginApp,
    resetMcpRedirectParamsStore,
    revokeMcpRedirectParams,
    validateMcpRedirectParams,
    switchOrganizationState,
    validateSwitchOrganizationState,
    resetSwitchOrganizationState
  }
}
